import { Avatar, Input, Table, TableColumnsType, Tabs, DatePicker } from "antd";
import { SearchOutlined, UserOutlined } from "@ant-design/icons";
import NextIcon from "components/Icons/NextIcon";
import PreviousIcon from "components/Icons/PreviousIcon";
import { sortIcon } from "components/Table";
import { ITripDriver } from "models/trips";
import moment from "moment";
import Status from "./components/Status";
import { TripRequestAlertContextProvider } from "./components/AlertContext";
import useTripRequest from "hooks/tripRequests/useTripRequest";
import { ITripRequest } from "models/tripRequest";
import TripRequestAction from "./components/Action";

export default function TripRequest() {
  const {
    data: { data, loading, page, per_page, search, total },
    statusFilter,
    dateFilter,
    handleSearch,
    handlePageChange,
    handleStatusChange,
    handleDateChange,
  } = useTripRequest();

  const columns: TableColumnsType<ITripRequest> = [
    {
      title: "#",
      key: "index",
      width: 60,
      render: (_: any, __: any, index: number) => {
        return (page - 1) * per_page + index + 1;
      },
    },
    {
      title: "Driver",
      dataIndex: "driver",
      render: (driver: ITripDriver) => (
        <div className="flex items-center gap-y-1 gap-x-1">
          <div className="w-full max-w-10 h-10">
            <Avatar
              shape="circle"
              size={40}
              src={driver?.image}
              icon={<UserOutlined />}
              className="max-w-10"
            />
          </div>
          <span className="whitespace-pre-line">
            {driver ? `${driver.first_name} ${driver.last_name}` : "No driver"}
          </span>
        </div>
      ),
      sortIcon,
    },
    {
      title: "Passenger",
      render: (tripRequest: ITripRequest) => (
        <div className="flex items-center gap-y-1 gap-x-1">
          <div className="w-full max-w-10 h-10">
            <Avatar shape="circle" size={40} src={tripRequest.passenger.image} icon={<UserOutlined />} />
          </div>
          <span className="whitespace-pre-line">
            {`${tripRequest.passenger.first_name} ${tripRequest.passenger.last_name}`}
          </span>
        </div>
      ),
      sortIcon,
    },
    {
      title: "Pickup Address",
      dataIndex: "pickup_address",
    },
    {
      title: "Drop off Address",
      dataIndex: "drop_off_address",
    },
    {
      title: "Requested At",
      dataIndex: "created_at",
      render: (createdAt) => (
        <span className="text-nowrap">{moment(createdAt).format("lll")}</span>
      ),
      sortIcon,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: Status,
    },
    {
      title: "Cancellation",
      render: (tripRequest: ITripRequest) => {
        const hasContent = tripRequest.cancel_type || tripRequest.cancel_reason;
        if (!hasContent) return <span className="text-gray-400">N/A</span>;
        
        return (
          <div className="flex flex-col gap-0.5 min-w-[140px]">
            {tripRequest.cancel_type && (
               <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[11px] font-bold uppercase text-red-600 bg-red-50 px-1.5 py-0.5 rounded w-fit border border-red-100">
                    {tripRequest.cancel_type.replace("_", " ")}
                  </span>
                  {tripRequest.canceled_by && (
                    <span className="text-[10px] text-gray-400 font-medium">
                      by {tripRequest.canceled_by === "account" ? (tripRequest.canceled_by_admin ? `${tripRequest.canceled_by_admin.first_name} ${tripRequest.canceled_by_admin.last_name}` : "Admin") : tripRequest.canceled_by}
                    </span>
                  )}
              </div>
            )}
            {tripRequest.cancel_reason && (
               <span className="text-xs text-gray-600 line-clamp-2 italic mt-0.5" title={tripRequest.cancel_reason}>
                "{tripRequest.cancel_reason}"
              </span>
            )}
          </div>
        );

      },
    },
    {
      title: "Outcome / Finisher",
      render: (request: ITripRequest) => {
        if (request.trip) {
          const trip = request.trip;
          const finisher = trip.admin_compete || trip.status_changed_by_admin;
          if (finisher) {
            return (
              <div className="flex items-center gap-x-1">
                <Avatar size={20} src={finisher.image} icon={<UserOutlined />} />
                <div className="flex flex-col">
                   <span className="text-[10px] font-bold text-blue-600 uppercase leading-none">Admin {trip.status}</span>
                   <span className="text-[11px] whitespace-nowrap">{finisher.first_name} {finisher.last_name}</span>
                </div>
              </div>
            );
          }
          if (trip.status === "completed") {
             return <span className="text-[11px] text-green-600 font-medium">Finished by Driver</span>;
          }
           return <span className="text-[11px] text-gray-400 italic capitalize">{trip.status}</span>;
        }
        return <span className="text-gray-400">-</span>;
      }
    },
    {
      title: "Action",
      render: (_, record) => <TripRequestAction tripRequest={record} />,
    },

  ];

  return (
    <TripRequestAlertContextProvider>
      <div className="w-full">
        <Tabs
          activeKey={statusFilter}
          onChange={handleStatusChange}
          className="px-6"
          items={[
            { key: "all", label: "All" },
            { key: "PENDING", label: "Pending" },
            { key: "ACCEPTED", label: "Accepted" },
            { key: "CANCELLED", label: "Canceled" },
          ]}
        />
        <div className="w-full px-6 flex flex-wrap-reverse gap-4">
          <Input
            className="max-w-72 w-full h-[40px] px-4 py-2 text-base border border-[rgb(0,0,0,0.1)] rounded-3xl"
            prefix={<SearchOutlined className="mr-2" />}
            placeholder="Search..."
            onChange={handleSearch}
            value={search}
          />
          <DatePicker
            className="h-[40px] rounded-3xl"
            format="YYYY-MM-DD"
            placeholder="All Time"
            allowClear
            value={dateFilter && dateFilter !== "all" ? moment(dateFilter, "YYYY-MM-DD") as any : null}
            onChange={(date, dateString) => handleDateChange(typeof dateString === 'string' ? dateString : (Array.isArray(dateString) ? dateString[0] : "all"))}
          />
        </div>
        <div className="w-full mt-8 px-4">
          <Table
            loading={loading}
            columns={columns}
            dataSource={data}
            onChange={handlePageChange}
            pagination={{
              total,
              pageSize: per_page,
              current: page,
              nextIcon: (
                <span className="flex items-center justify-center w-8 h-8 text-2xl">
                  <NextIcon />
                </span>
              ),
              prevIcon: (
                <span className="flex items-center justify-center w-8 h-8 text-2xl">
                  <PreviousIcon />
                </span>
              ),
            }}
          />
        </div>
      </div>
    </TripRequestAlertContextProvider>
  );
}
